import { ComponentType } from 'react'
import { MatchesPanel } from './MatchesPanel'
import { FAQPanel } from './FAQPanel'

/**
 * Single source of truth for which modules can appear as tabs and what each one
 * renders. The `match_tabs_module.tabs` flag references these by `module` key to
 * control presence/order, and supplies the tab `title`; any entry whose module
 * isn't in this registry is dropped by `validateTabs` (the flag can't add tabs
 * that don't exist in code).
 *
 * `defaultLabel` is the fallback used when the flag omits/blanks a `title`.
 * `action` is the analytics value sent on `handoff-faq-click` — it lives here
 * (not in the flag) so relabeling a tab can't change the tracking contract.
 *
 * To add a tab: add a content variable to moduleRegistry, register it here with
 * a defaultLabel/action/Panel, then reference its key in the flag's `tabs`.
 */
export interface TabDef {
  /** Label used when the flag's `title` is missing or blank. */
  defaultLabel: string
  /** Value sent as the `action` property on the `handoff-faq-click` event. */
  action: string
  /** Panel content; reads its own content variable via useModuleConfig. */
  Panel: ComponentType
}

export const TAB_REGISTRY = {
  advisor_cards_module: { defaultLabel: 'Compare Your Matches', action: 'Matches', Panel: MatchesPanel },
  faq_module: { defaultLabel: 'FAQs', action: 'FAQ', Panel: FAQPanel }
} satisfies Record<string, TabDef>

export type TabKey = keyof typeof TAB_REGISTRY

/** A tab that survived validation: a known module key + its resolved label. */
export interface ResolvedTab {
  key: TabKey
  label: string
}

export const isTabKey = (key: unknown): key is TabKey =>
  typeof key === 'string' && key in TAB_REGISTRY

/**
 * Filters a raw flag value into ordered, render-ready tabs. Non-array input
 * yields `[]`. Each entry must be an object with a `module` present in
 * TAB_REGISTRY; its label is the entry's `title` (when a non-empty string) or
 * the registry default. Entries with an unknown/missing module are dropped with
 * a warning
 */
export const validateTabs = (raw: unknown): ResolvedTab[] => {
  const list = Array.isArray(raw) ? raw : []
  const resolved: ResolvedTab[] = []
  for (const entry of list) {
    const module = entry && typeof entry === 'object' ? (entry as { module?: unknown }).module : undefined
    if (isTabKey(module)) {
      const title = (entry as { title?: unknown }).title
      const label = typeof title === 'string' && title.trim() !== ''
        ? title
        : TAB_REGISTRY[module].defaultLabel
      resolved.push({ key: module, label })
    } else {
      console.warn(`[MatchTabsModule] Ignoring tab with unknown/missing module: ${JSON.stringify(entry)}`)
    }
  }
  return resolved
}
